import React, { useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { isEmpty } from 'lodash'
import { useParams } from 'react-router-dom'

import { ReactComponent as ImageDev } from 'assets/images/development-empty.svg'

import { DEFAULT_DEV_PLAN_STEPS } from 'utils/helpers/course-component'
import { getPercentage } from 'utils/helpers'

import Card from 'components/Card'
import Button from 'components/Button'
import PageLoader from 'components/PageLoader'
import LineChart from 'components/LineChart'
import Filters from 'components/Filters'
import ProgressCard from 'components/ProgressCard'

import singleUserStyles from 'pages/Coach/UserManagement/SingleUser/styles.module.scss'

import DevPlanCard from './DevPlanCard'
import { chartYTickValues } from './constants'
import { useDevPlan, useStatistics } from './hooks'

import Add from './DevPlanCard/Add'

const filters = [
  {
    name: 'filter',
    options: [
      {
        value: 'this-week',
        label: 'This Week'
      },
      {
        value: 'past-week',
        label: 'Past Week'
      },
      {
        value: 'past-month',
        label: 'Past Month'
      }
    ]
  }
]

const DevelopmentPlan = () => {
  const { userId } = useParams()
  const [addMode, setAddMode] = useState(false)
  const [filterData, setFilterData] = useState('this-week')
  const [mutableSteps, setMutableSteps] = useState([])
  const {
    getDevPlan,
    addPlanGoal,
    addPlanGoalIsLoading,
    data,
    editSteps,
    editPlanGoalLoading,
    reorderGoals
  } = useDevPlan(userId)

  const { statistics, statFetch } = useStatistics({
    filterData,
    userId
  })

  const planGoals =
    (data &&
      [...data.planGoals].sort(
        (a, b) => a.sortOrder - b.sortOrder
      )) ||
    []
  const emptyState = !planGoals.length && !addMode
  const completedSteps = mutableSteps.filter(step => step.status)
  const progress = getPercentage({
    a: completedSteps.length,
    b: mutableSteps.length,
    noDemical: true
  })

  const toggleAddMode = () => setAddMode(!addMode)
  const moveGoal = (index, direction) => {
    const target = index + direction
    if (target < 0 || target >= planGoals.length) return
    const reordered = [...planGoals]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(target, 0, moved)
    reorderGoals({
      userId,
      orderedGoalIds: reordered.map(goal => goal.planId)
    })
  }
  const stepOnChange = (stepId, value) => {
    const selectedStep = mutableSteps.find(
      step => step.id === stepId
    )
    const changedStep = {
      ...selectedStep,
      status: value
    }
    const filteredSteps = mutableSteps.filter(
      step => step.id !== stepId
    )
    const sortedSteps = [changedStep, ...filteredSteps].sort(
      (a, b) => a.id - b.id
    )
    setMutableSteps(sortedSteps)
    editSteps({ userId, payload: { steps: sortedSteps } })
  }
  const addGoalButtonClick = value => {
    const { title, actionSteps } = value
    const payload = {
      title,
      actionSteps: actionSteps.map(step => step.description)
    }
    addPlanGoal({ userId, payload })
    toggleAddMode()
  }

  useEffect(() => {
    const steps =
      data && !isEmpty(data.steps)
        ? data.steps
        : DEFAULT_DEV_PLAN_STEPS

    setMutableSteps(steps)
    statFetch()
  }, [data, statFetch])

  useEffect(() => {
    getDevPlan()
  }, [getDevPlan, userId])

  const statisticsActionSteps = statistics?.statistics
    ?.map(x => x.actionSteps.length)
    .filter(x => x !== 0)
  const hasActionSteps = statisticsActionSteps?.length > 0

  return (
    <PageLoader loader={editPlanGoalLoading}>
      <div className={singleUserStyles.userManagement}>
        <Row className="mb-40">
          <Col
            xs={6}
            className={singleUserStyles.courseProgress}
          >
            <ProgressCard
              steps={mutableSteps}
              progress={progress}
              onChange={stepOnChange}
            />
          </Col>
          <Col xs={6}>
            <Card outlined className="h-100">
              Based on the Three Pillars of Leadership 360
              report, Personality and Leadership profile,
              informal feedback, and personal reflection, several
              leadership development goals are identified.
            </Card>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Row className="mb-10">
              <Col xs={6} className="d-flex align-items-center">
                <p
                  className={`${singleUserStyles.sectionTitle} mb-0`}
                >
                  Development Plan
                </p>
              </Col>
              <Col
                xs={6}
                className="d-flex align-items-center justify-content-end"
              >
                {!emptyState && (
                  <Button
                    variant="secondary"
                    className="d-flex align-items-center"
                    onClick={toggleAddMode}
                    disabled={addMode}
                  >
                    <FontAwesomeIcon
                      icon={faPlus}
                      className={`${singleUserStyles.addIcon} mr-8`}
                    />
                    Goal
                  </Button>
                )}
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                {addMode && (
                  <Add
                    submit={addGoalButtonClick}
                    toggleAddMode={toggleAddMode}
                    isLoading={addPlanGoalIsLoading}
                  />
                )}
                {planGoals.map((plan, index) => (
                  <DevPlanCard
                    key={plan.planId}
                    planId={plan.planId}
                    title={plan.title}
                    actionSteps={plan.actionSteps}
                    date={plan.date}
                    onMoveUp={() => moveGoal(index, -1)}
                    onMoveDown={() => moveGoal(index, 1)}
                    isFirst={index === 0}
                    isLast={index === planGoals.length - 1}
                  />
                ))}
                {emptyState && (
                  <Card
                    className="d-flex align-items-center justify-content-center flex-column"
                    style={{ minHeight: 350 }}
                  >
                    <ImageDev className="mb-20" />
                    <div className="text-gray-dark">
                      Here you will find your goals for the day.
                    </div>
                    <div className="text-gray-dark mb-20">
                      Start planning your first set of goals now.
                    </div>
                    <Button
                      variant="secondary"
                      onClick={toggleAddMode}
                      disabled={addMode}
                    >
                      Start Planning
                    </Button>
                  </Card>
                )}
              </Col>
            </Row>
            {!!statistics?.statistics.length && (
              <>
                <Row className="mb-10 mt-20">
                  <Col xs={12} lg={6}>
                    <p
                      className={`${singleUserStyles.sectionTitle} mb-0`}
                    >
                      Track Progress
                    </p>
                  </Col>
                  <Col
                    xs={12}
                    lg={6}
                    className="d-block d-lg-flex justify-content-end"
                  >
                    <p>
                      <Filters
                        filters={filters}
                        filterValue={filterData}
                        onFilterChange={(name, value) => {
                          setFilterData(value)
                        }}
                      />
                    </p>
                  </Col>
                </Row>
                <Card>
                  <Row>
                    <Col lg={12}>
                      <LineChart
                        data={statistics.statistics}
                        yDomain={[1, 10]}
                        yTickValues={chartYTickValues}
                        hideXAxis={!hasActionSteps}
                      />
                    </Col>
                  </Row>
                </Card>
              </>
            )}
          </Col>
        </Row>
      </div>
    </PageLoader>
  )
}

DevelopmentPlan.propTypes = {}

export default DevelopmentPlan
